import React, { useMemo } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  GTG_TARGET_COUNTRY_ES,
  GTG_TARGET_COUNTRY_TH,
  normalizeGtgTargetCountry,
} from "@healthdesk/shared-utils";
import {
  getGtgProvinceDisplayName,
  getGtgProvinceGeoRow,
  getGtgProvinceHealthRow,
  GTG_PROVINCE_GEO_VINTAGE_CAPTION,
  GTG_PROVINCE_GOOGLE_ADS_NOTE,
  SPAIN_HEALTH_STATS_CAPTION,
  THAILAND_HEALTH_STATS_CAPTION,
} from "../../services/census/getGtgProvinceMapContext";

function fmtDecimal(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function fmtInt(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Math.round(Number(n)).toLocaleString();
}

/**
 * Spain / Thailand province: geo (Natural Earth) + official stats bundle; Add to list (same flow as Kenya county modal).
 */
const GtgProvinceDetailModal = ({
  open,
  targetCountry,
  province,
  isSelected,
  onClose,
  onAddOrRemove,
}) => {
  const tc = normalizeGtgTargetCountry(targetCountry);
  const canon = typeof province === "string" ? province.trim() : "";
  const displayName = canon ? getGtgProvinceDisplayName(canon) : "";
  const geo = useMemo(
    () => (canon && (tc === GTG_TARGET_COUNTRY_ES || tc === GTG_TARGET_COUNTRY_TH) ? getGtgProvinceGeoRow(tc, canon) : null),
    [canon, tc]
  );
  const health = useMemo(
    () => (canon && (tc === GTG_TARGET_COUNTRY_ES || tc === GTG_TARGET_COUNTRY_TH) ? getGtgProvinceHealthRow(tc, canon) : null),
    [canon, tc]
  );

  const countryLabel = tc === GTG_TARGET_COUNTRY_ES ? "Spain" : tc === GTG_TARGET_COUNTRY_TH ? "Thailand" : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle component="div" sx={{ fontWeight: 700 }}>
        {displayName || canon || "Province"}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {countryLabel} province (Google Ads geo target). Figures describe the administrative area, not individual
          patients. {GTG_PROVINCE_GOOGLE_ADS_NOTE}
        </Typography>

        {tc === GTG_TARGET_COUNTRY_ES ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {SPAIN_HEALTH_STATS_CAPTION}
          </Typography>
        ) : tc === GTG_TARGET_COUNTRY_TH ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {THAILAND_HEALTH_STATS_CAPTION}
          </Typography>
        ) : null}

        {health && tc === GTG_TARGET_COUNTRY_ES ? (
          <Stack spacing={1.25} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Official demographics & mortality (Eurostat)
            </Typography>
            <StatRow label="Population on 1 January" value={fmtInt(health.totalPopulation)} />
            <StatRow label="Crude death rate (‰)" value={fmtDecimal(health.crudeDeathRatePer1000)} />
            <StatRow label="Crude birth rate (‰)" value={fmtDecimal(health.crudeBirthRatePer1000)} />
            <StatRow label="Deaths (annual)" value={fmtInt(health.deathsTotal)} />
            <StatRow label="Live births (annual)" value={fmtInt(health.liveBirthsTotal)} />
          </Stack>
        ) : null}

        {health && tc === GTG_TARGET_COUNTRY_TH ? (
          <Stack spacing={1.25} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Official census demographics (NSO)
            </Typography>
            <StatRow label="Population (census 2010)" value={fmtInt(health.totalPopulation)} />
            <StatRow label="Female share of population" value={fmtPct(health.pctFemalePopulation)} />
          </Stack>
        ) : null}

        {!geo ? (
          <Typography variant="body2" color="text.secondary">
            No bundled geo row for this province key.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Area context (Natural Earth)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {GTG_PROVINCE_GEO_VINTAGE_CAPTION}
            </Typography>
            <StatRow label="Land area (km², sphere approx.)" value={fmtDecimal(geo.landAreaSqKm)} />
            <StatRow
              label="Map marker (lat, lng)"
              value={`${geo.lat.toFixed(4)}, ${geo.lng.toFixed(4)}`}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ pt: 0.5 }}>
              NE feature name: <strong>{geo.matchedNaturalEarthName}</strong>
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          color={isSelected ? "secondary" : "primary"}
          disabled={!canon}
          onClick={() => {
            if (canon) onAddOrRemove(canon);
            onClose();
          }}
        >
          {isSelected ? "Remove from list" : "Add to list"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function StatRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
}

function fmtPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

export default GtgProvinceDetailModal;
